import React, { useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Card, Button, Table, Form, Modal, Badge } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { Plus } from 'react-bootstrap-icons';
import EMMSectionHeader from './EMMSectionHeader';
import { useAccount } from '../contexts/AccountContext';
import { useGetUserPreferencesQuery } from '../services/api';
import {
  getTenantTimezoneFromPreferencesData,
  toLocaleDateStringInTimezone,
} from '../utils/tenantTimezone';
import { useUserPreferencesQueryArg } from '../hooks/useTenantIdForPreferences';

const Subscribers = () => {
  const { selectedAccount, isAllAccountsSelected } = useAccount();
  const userPreferencesQueryArg = useUserPreferencesQueryArg();
  const { data: userPreferencesData } = useGetUserPreferencesQuery(userPreferencesQueryArg);
  const tenantTimezone = useMemo(
    () => getTenantTimezoneFromPreferencesData(userPreferencesData),
    [userPreferencesData]
  );
  const [subscribers, setSubscribers] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newSubscriber, setNewSubscriber] = useState({ email: '', name: '' });

  useEffect(() => {
    // TODO: Fetch subscribers from API - ACCOUNT SPECIFIC
    // No mock data - show empty state until real API is connected
    setSubscribers([]);
  }, [selectedAccount.id, isAllAccountsSelected]);

  const handleAddSubscriber = async (e) => {
    e.preventDefault();
    try {
      // TODO: Implement subscriber addition with API
      const subscriber = {
        id: subscribers.length + 1,
        ...newSubscriber,
        status: 'active',
        subscribedAt: new Date().toISOString()
      };
      
      setSubscribers([...subscribers, subscriber]);
      setNewSubscriber({ email: '', name: '' });
      setShowAddModal(false);
      toast.success('Subscriber added successfully!');
    } catch (error) {
      toast.error('Failed to add subscriber');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: 'success',
      unsubscribed: 'danger',
      pending: 'warning'
    };
    return (
      <span className={`badge bg-${variants[status] || 'secondary'}`}>
        {status}
      </span>
    );
  };

  const formatSubscribedDate = (value) =>
    toLocaleDateStringInTimezone(
      value,
      'en-US',
      { month: 'short', day: 'numeric', year: 'numeric' },
      tenantTimezone
    ) || '—';

  return (
    <>
      <EMMSectionHeader title="Subscribers" showHelp />
      <Container className="mt-3">
      {/* Action bar - same spot and size as Clients "+ Client" button */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center gap-2">
          <Button
            variant="primary"
            size="sm"
            className="d-flex align-items-center"
            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
            onClick={() => setShowAddModal(true)}
          >
            <Plus className="me-1" size={14} />
            Subscriber
          </Button>
          <span className="text-muted" style={{ fontSize: '0.75rem' }}>{subscribers.length} subscribers</span>
        </div>
      </div>
      <Row>
        <Col md={4}>
          <Card>
            <Card.Body className="text-center">
              <h3 className="text-primary">{subscribers.length}</h3>
              <p className="text-muted">Total Subscribers</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card>
            <Card.Body className="text-center">
              <h3 className="text-success">
                {subscribers.filter(s => s.status === 'active').length}
              </h3>
              <p className="text-muted">Active</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card>
            <Card.Body className="text-center">
              <h3 className="text-danger">
                {subscribers.filter(s => s.status === 'unsubscribed').length}
              </h3>
              <p className="text-muted">Unsubscribed</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mt-4">
        <Col>
          <Card>
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <h5>Subscriber List</h5>
                <Form.Control
                  type="text"
                  placeholder="Search subscribers..."
                  style={{ width: '300px' }}
                />
              </div>
            </Card.Header>
            <Card.Body>
              <Table responsive>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Subscribed</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {subscribers.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="text-center text-muted py-4">
                        <p>No subscribers found</p>
                        <small>Add your first subscriber to get started.</small>
                      </td>
                    </tr>
                  ) : (
                    subscribers.map((subscriber) => (
                      <tr key={subscriber.id}>
                        <td>{subscriber.name}</td>
                        <td>{subscriber.email}</td>
                        <td>{getStatusBadge(subscriber.status)}</td>
                        <td>{formatSubscribedDate(subscriber.subscribedAt)}</td>
                        <td>
                          <Button size="sm" variant="outline-primary" className="me-2">
                            Edit
                          </Button>
                          <Button size="sm" variant="outline-danger">
                            Remove
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Add Subscriber Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add New Subscriber</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleAddSubscriber}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Email Address *</Form.Label>
              <Form.Control
                type="email"
                placeholder="subscriber@example.com"
                value={newSubscriber.email}
                onChange={(e) => setNewSubscriber({...newSubscriber, email: e.target.value})}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Subscriber Name"
                value={newSubscriber.name}
                onChange={(e) => setNewSubscriber({...newSubscriber, name: e.target.value})}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Add Subscriber
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
    </>
  );
};

export default Subscribers;

